// Third party
import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import { AnnouncementSource, UserAnnouncement } from '../../../../redux/modules/types/announcements';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';
import { READ_AND_ACKNOWLEDGE_ANNOUNCEMENT } from '../../../../redux/modules/announcements/user';

// Utils
import { formatLocalizedString, FormattedMessage, formatMessage } from '../../../../utils/i18n';

// Components
import { CloseButton, DestructiveButton } from '../../buttons';
import NotificationBody from '../../notification/NotificationBody';
import Modal from '@/views/components/modals/AccessibleModal';

type StateProps = {
  announcement?: UserAnnouncement;
  acceptingAcknowledgment?: boolean;
};

type Props = StateProps & {
  announcementId?: string;
  onCancel: () => void;
  onAccept: () => void;
  onClosed: () => void;
  show?: boolean;
};

type State = {
  isReaded: boolean;
};

class NotificationDetailsModal extends React.Component<Props, State> {
  textRef = React.createRef<any>();

  private initialState: State = {
    isReaded: false,
  };

  readonly state: State = this.initialState;

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    if (prevState.isReaded) return;
    if (this.textRef.current && this.textRef.current.scrollHeight <= this.textRef.current.clientHeight) {
      this.setState({ isReaded: true });
    }
  }

  handleScroll = (event: React.BaseSyntheticEvent) => {
    if (event.target.scrollHeight - event.target.scrollTop === event.target.clientHeight) {
      this.setState({ isReaded: true });
    }
  };

  onCancel = () => {
    this.props.onCancel();
    this.setState(this.initialState);
  };

  onClosed = () => {
    this.props.onClosed();
    this.setState(this.initialState);
  };

  render() {
    const { onAccept, show, announcement, acceptingAcknowledgment } = this.props;

    if (!announcement) {
      return null;
    }

    return (
      <Modal show={show} onHide={this.onCancel} onExited={this.onClosed} className="notification-details-modal modal" centered>
        <Modal.Header>
          <Modal.Title>
            <i className={`icon-${!announcement.acknowledgmentRequired ? 'info-outline' : 'warning'}`} aria-hidden="true" />
            {formatLocalizedString(announcement.title)}

            <p>
              <span className="me-2">{moment(announcement.notificationDate).format(formatMessage('Common.DateFormats.DateWithTime'))}</span>
              {announcement.source === AnnouncementSource.ENTERPRISE && <i className="icon-enterprise" />}
            </p>
          </Modal.Title>
          {!!this.onCancel && <CloseButton onClick={this.onCancel} />}
        </Modal.Header>

        <Modal.Body>
          <div ref={this.textRef} className="modal-body-text" onScroll={this.handleScroll}>
            <NotificationBody item={announcement} totalText />
          </div>
        </Modal.Body>

        {announcement.acknowledgmentRequired && !announcement.acknowledged && (
          <Modal.Footer>
            <DestructiveButton onClick={onAccept} loading={acceptingAcknowledgment} className="flex-fill" disabled={!this.state.isReaded}>
              <FormattedMessage id="AnnouncementsAdvisor.Acknowledge" />
            </DestructiveButton>
          </Modal.Footer>
        )}
      </Modal>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: Props): StateProps => ({
  announcement: state.userAnnouncements.announcements.find(announcement => announcement._id === ownProps.announcementId),
  acceptingAcknowledgment: selectIsLoadingEvery(state, [READ_AND_ACKNOWLEDGE_ANNOUNCEMENT.type]),
});

export default connect(mapStateToProps)(NotificationDetailsModal);
